// ── FalloutDetailPage — lost opportunities: termination-reason table + detail ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { isExcludedOwner, OwnerMultiSelect } from "./analyticsShared";

const commit = (x) => parseFloat(x.commitment_amount || 0);

export const FalloutDetailPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);
  const [stageSel, setStageSel] = useState("");
  const [industrySel, setIndustrySel] = useState("");
  const [stateSel, setStateSel] = useState("");
  const [typeSel, setTypeSel] = useState("");
  const [reason, setReason] = useState("");

  const load = () => {
    setLoading(true);
    api.get("getOpportunities").then(o => { setOpps(Array.isArray(o) ? o : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const lost = useMemo(() => opps.filter(o => o.is_closed && !o.is_won), [opps]);
  const owners = useMemo(() => [...new Set(lost.map(o => o.ownerName).filter(Boolean))].sort(), [lost]);
  const sources = useMemo(() => [...new Set(lost.map(o => o.lead_source).filter(Boolean))].sort(), [lost]);
  const stages = useMemo(() => [...new Set(lost.map(o => o.stage).filter(Boolean))].sort(), [lost]);
  const industries = useMemo(() => [...new Set(lost.map(o => o.account_industry).filter(Boolean))].sort(), [lost]);
  const states = useMemo(() => [...new Set(lost.map(o => o.account_state).filter(Boolean))].sort(), [lost]);
  const types = useMemo(() => [...new Set(lost.map(o => o.opportunity_type).filter(Boolean))].sort(), [lost]);

  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const rows = useMemo(() => {
    const c = company.trim().toLowerCase();
    return lost.filter(o => {
      if (!ownerOk(o.ownerName)) return false;
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (stageSel && o.stage !== stageSel) return false;
      if (industrySel && o.account_industry !== industrySel) return false;
      if (stateSel && o.account_state !== stateSel) return false;
      if (typeSel && o.opportunity_type !== typeSel) return false;
      if (reason && (o.termination_reason || "Unspecified") !== reason) return false;
      return true;
    });
  }, [lost, company, sourceSel, ownerSel, stageSel, industrySel, stateSel, typeSel, reason]);

  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(rows, 'companyname', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  // termination-reason breakdown (over rows, ignoring the reason click-filter itself)
  const reasonBase = (() => {
    const c = company.trim().toLowerCase();
    return lost.filter(o => {
      if (!ownerOk(o.ownerName)) return false;
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (stageSel && o.stage !== stageSel) return false;
      if (industrySel && o.account_industry !== industrySel) return false;
      if (stateSel && o.account_state !== stateSel) return false;
      if (typeSel && o.opportunity_type !== typeSel) return false;
      return true;
    });
  })();
  const rMap = new Map();
  reasonBase.forEach(o => {
    const k = o.termination_reason || "Unspecified";
    const e = rMap.get(k) || { key: k, cnt: 0, amt: 0 };
    e.cnt += 1; e.amt += commit(o); rMap.set(k, e);
  });
  const byReason = [...rMap.values()].sort((a, b) => b.cnt - a.cnt || b.amt - a.amt);
  const rTotCnt = reasonBase.length;
  const rTotAmt = reasonBase.reduce((s, o) => s + commit(o), 0);

  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" };
  const tf = { padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", background: "var(--color-background-secondary)" };
  const tfr = { ...tf, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const detailCols = ["companyname", "termination_reason", "lead_source", "account_industry", "account_state", "commitment_amount", "stage"];

  return <>
    <SectionHeader title="Fallout Detail" actions={[
      <ExportBtn key="ex" onClick={() => exportCSV(detailSorted, detailCols, "fallout_detail.csv")}>Export CSV</ExportBtn>
    ]} />

    {/* slicers */}
    <Card style={{ padding: 10, marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 150 }} />
      <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
      <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
      <select value={stageSel} onChange={e => setStageSel(e.target.value)} style={selStyle}><option value="">All Stages</option>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select>
      <select value={industrySel} onChange={e => setIndustrySel(e.target.value)} style={selStyle}><option value="">All Industries</option>{industries.map(s => <option key={s} value={s}>{s}</option>)}</select>
      <select value={stateSel} onChange={e => setStateSel(e.target.value)} style={selStyle}><option value="">All States</option>{states.map(s => <option key={s} value={s}>{s}</option>)}</select>
      <select value={typeSel} onChange={e => setTypeSel(e.target.value)} style={selStyle}><option value="">All Opp Types</option>{types.map(s => <option key={s} value={s}>{s}</option>)}</select>
      {reason && <button onClick={() => setReason("")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>{reason} <span style={{ fontWeight: 700 }}>✕</span></button>}
    </Card>

    <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, alignItems: "start" }}>
      {/* termination reason table */}
      <Card style={{ padding: 0, overflowX: "auto" }}>
        <div style={cardTitle}>Termination Reason</div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
          <thead><tr><Th>Reason</Th><Th style={{ textAlign: "right" }}>Cnt</Th><Th style={{ textAlign: "right" }}>Amount</Th></tr></thead>
          <tbody>{byReason.length === 0 ? <tr><Td colSpan={3} style={{ color: "var(--color-text-secondary)" }}>None.</Td></tr> : byReason.map(r => {
            const active = reason === r.key;
            return (
              <tr key={r.key} title="Click to filter" style={{ cursor: "pointer", background: active ? B.cyanLight : "" }}
                onClick={() => setReason(prev => prev === r.key ? "" : r.key)}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--color-background-secondary)"; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = ""; }}>
                <Td style={{ fontWeight: active ? 700 : 400 }}>{r.key}</Td>
                <TdNum>{r.cnt}</TdNum>
                <TdNum>{fmt(r.amt)}</TdNum>
              </tr>
            );
          })}</tbody>
          <tfoot><tr>
            <td style={tf}>Total</td>
            <td style={tfr}>{rTotCnt}</td>
            <td style={tfr}>{fmt(rTotAmt)}</td>
          </tr></tfoot>
        </table>
      </Card>

      {/* detail */}
      <Card style={{ overflowX: "auto", minWidth: 0 }}>
        <div style={cardTitle}>Lost Opportunity Detail</div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
          <thead><tr>
            <STh label="Opportunity Name" sortKey="companyname"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Termination Reason" sortKey="termination_reason" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Lead Source"   sortKey="lead_source"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Industry"      sortKey="account_industry"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="State"         sortKey="account_state"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Amount"        sortKey="commitment_amount"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="Stage"         sortKey="stage"                 currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          </tr></thead>
          <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={7} style={{ color: "var(--color-text-secondary)" }}>No lost opportunities.</Td></tr> : detailSorted.map(o => (
            <tr key={o.id} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
              <Td style={{ fontWeight: 500, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.companyname || o.name || ""}>{o.companyname || o.name || "—"}</Td>
              <Td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.termination_reason || ""}>{o.termination_reason || "—"}</Td>
              <Td>{o.lead_source || "—"}</Td>
              <Td style={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.account_industry || ""}>{o.account_industry || "—"}</Td>
              <Td>{o.account_state || "—"}</Td>
              <TdNum>{fmt(commit(o))}</TdNum>
              <Td>{o.stage || "—"}</Td>
            </tr>
          ))}</tbody>
          <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
            <td colSpan={5} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, o) => s + commit(o), 0))}</td>
            <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
          </tr></tfoot>
        </table>
      </Card>
    </div>
  </>;
};
